import React, { useState } from 'react';
import { 
  X, 
  Calendar, 
  MessageSquare, 
  Heart, 
  Share2, 
  Trash2,
  User,
  Image,
  Video,
  AlertTriangle,
  Eye,
  EyeOff
} from 'lucide-react';
import Swal from 'sweetalert2';

const PublicationDetail = ({ publication, onClose, onDeleteComment, onDeletePublication, onToggleVisibility }) => {
  const [activeTab, setActiveTab] = useState('info');

  if (!publication) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getHumeurEmoji = (humeur) => {
    const humeurs = {
      'HEUREUX': '😊 Heureux',
      'TRISTE': '😢 Triste',
      'EXCITE': '🤩 Excité',
      'FATIGUE': '😩 Fatigué',
      'AMOUREUX': '😍 Amoureux',
      'EN_COLERE': '😠 En colère',
      'CELEBRER': '🎉 Célébrer',
      'NOSTALGIQUE': '🥺 Nostalgique',
      'MALADE': '🤒 Malade',
      'SURPRIS': '😲 Surpris',
      'COOL': '😎 Cool',
      'INQUIET': '😟 Inquiet'
    };
    return humeurs[humeur] || humeur;
  };

  const handleDeletePublication = async () => {
    const result = await Swal.fire({
      title: 'Supprimer cette publication ?',
      text: "Cette action est irréversible !",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#FF6B00',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      customClass: {
        popup: 'rounded-xl',
        confirmButton: 'rounded-lg px-6 py-2',
        cancelButton: 'rounded-lg px-6 py-2'
      }
    });

    if (result.isConfirmed) {
      onDeletePublication(publication);
    }
  };

  const handleToggleVisibility = async () => {
    const result = await Swal.fire({
      title: `${publication.est_masquee ? 'Afficher' : 'Masquer'} cette publication ?`,
      text: publication.est_masquee 
        ? "La publication sera visible par tous les utilisateurs." 
        : "La publication sera masquée pour tous les utilisateurs.",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#FF6B00',
      cancelButtonColor: '#6B7280',
      confirmButtonText: publication.est_masquee ? 'Oui, afficher' : 'Oui, masquer',
      cancelButtonText: 'Annuler',
      customClass: {
        popup: 'rounded-xl',
        confirmButton: 'rounded-lg px-6 py-2',
        cancelButton: 'rounded-lg px-6 py-2'
      }
    });

    if (result.isConfirmed) {
      onToggleVisibility(publication);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-[#FF6B00] to-[#437011]">
              <h2 className="text-2xl font-bold text-white">Détails de la publication</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              >
                <X size={24} className="text-white" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 bg-gray-50">
              <button
                onClick={() => setActiveTab('info')}
                className={`flex-1 px-6 py-3 font-medium transition-colors ${
                  activeTab === 'info'
                    ? 'text-[#FF6B00] border-b-2 border-[#FF6B00] bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Informations
              </button>
              <button
                onClick={() => setActiveTab('commentaires')}
                className={`flex-1 px-6 py-3 font-medium transition-colors flex items-center justify-center gap-2 ${
                  activeTab === 'commentaires'
                    ? 'text-[#FF6B00] border-b-2 border-[#FF6B00] bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <MessageSquare size={18} />
                Commentaires ({publication.commentaires_count || 0})
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'info' && (
                <div className="space-y-6">
                  {/* Utilisateur */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <User size={18} className="text-[#FF6B00]" />
                      <h3 className="font-semibold text-gray-900">Auteur</h3>
                    </div>
                    <div className="flex items-center gap-3 ml-7">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF6B00] to-[#437011] flex items-center justify-center text-white font-bold text-lg">
                        {publication.utilisateur.first_name?.[0] || publication.utilisateur.username?.[0] || 'U'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {publication.utilisateur.first_name && publication.utilisateur.last_name
                            ? `${publication.utilisateur.first_name} ${publication.utilisateur.last_name}`
                            : publication.utilisateur.username}
                        </p>
                        <p className="text-sm text-gray-600">{publication.utilisateur.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar size={18} className="text-blue-600" />
                        <h3 className="font-semibold text-blue-900">Créée le</h3>
                      </div>
                      <p className="text-blue-700 ml-7">{formatDate(publication.created_at)}</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar size={18} className="text-purple-600" />
                        <h3 className="font-semibold text-purple-900">Modifiée le</h3>
                      </div>
                      <p className="text-purple-700 ml-7">{formatDate(publication.updated_at)}</p>
                    </div>
                  </div>

                  {/* Statut & Humeur */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`${publication.est_masquee ? 'bg-red-50' : 'bg-green-50'} rounded-lg p-4`}>
                      <div className="flex items-center gap-2 mb-2">
                        {publication.est_masquee ? <EyeOff size={18} className="text-red-600" /> : <Eye size={18} className="text-green-600" />}
                        <h3 className={`font-semibold ${publication.est_masquee ? 'text-red-900' : 'text-green-900'}`}>
                          Visibilité
                        </h3>
                      </div>
                      <p className={`${publication.est_masquee ? 'text-red-700' : 'text-green-700'} ml-7`}>
                        {publication.est_masquee ? 'Masquée' : 'Visible'}
                      </p>
                    </div>
                    {publication.humeur && (
                      <div className="bg-yellow-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xl">{getHumeurEmoji(publication.humeur).split(' ')[0]}</span>
                          <h3 className="font-semibold text-yellow-900">Humeur</h3>
                        </div>
                        <p className="text-yellow-700 ml-7">{getHumeurEmoji(publication.humeur)}</p>
                      </div>
                    )}
                  </div>

                  {/* Signalements */}
                  {publication.nombre_signalements > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle size={18} className="text-red-600" />
                        <h3 className="font-semibold text-red-900">Signalements</h3>
                      </div>
                      <p className="text-red-700 ml-7">
                        Cette publication a été signalée <strong>{publication.nombre_signalements}</strong> fois
                      </p>
                    </div>
                  )}

                  {/* Contenu */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Contenu</h3>
                    {publication.texte ? (
                      <p 
                        className="text-gray-700 whitespace-pre-wrap p-4 rounded"
                        style={{ backgroundColor: publication.background_color !== '#FFFFFF' ? publication.background_color : 'transparent' }}
                      >
                        {publication.texte}
                      </p>
                    ) : (
                      <p className="text-gray-400 italic">Aucun texte</p>
                    )}
                  </div>

                  {/* Médias */}
                  {publication.medias && publication.medias.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Image size={18} className="text-[#FF6B00]" />
                        <h3 className="font-semibold text-gray-900">Médias ({publication.medias.length})</h3>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {publication.medias.map((media) => (
                          <div key={media.id} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden">
                            {media.type === 'image' ? (
                              <img 
                                src={`http://192.168.1.65:8000${media.fichier}`}
                                alt="Publication media" 
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/300?text=Image+non+disponible';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center bg-gray-200">
                                <Video size={32} className="text-gray-400 mb-2" />
                                <span className="text-xs text-gray-500">Vidéo</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Statistiques */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-red-50 rounded-lg p-4 text-center">
                      <Heart size={24} className="text-red-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-red-700">{publication.reactions_count || 0}</p>
                      <p className="text-sm text-red-600">Réactions</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <MessageSquare size={24} className="text-blue-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-blue-700">{publication.commentaires_count || 0}</p>
                      <p className="text-sm text-blue-600">Commentaires</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <Share2 size={24} className="text-green-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-green-700">{publication.jaimes_count || 0}</p>
                      <p className="text-sm text-green-600">Partages</p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'commentaires' && (
                <div className="space-y-4">
                  {publication.commentaires && publication.commentaires.length > 0 ? (
                    publication.commentaires.map((comment) => (
                      <div key={comment.id} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                              {comment.utilisateur?.first_name?.[0] || comment.utilisateur?.username?.[0] || 'U'}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-semibold text-gray-900">
                                  {comment.utilisateur?.first_name && comment.utilisateur?.last_name
                                    ? `${comment.utilisateur.first_name} ${comment.utilisateur.last_name}`
                                    : comment.utilisateur?.username || 'Utilisateur'}
                                </p>
                                <span className="text-xs text-gray-500">
                                  {formatDate(comment.created_at)}
                                </span>
                              </div>
                              <p className="text-gray-700 whitespace-pre-wrap">{comment.texte}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => onDeleteComment && onDeleteComment(comment)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Supprimer le commentaire"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <MessageSquare size={48} className="text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">Aucun commentaire pour cette publication</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer - Actions */}
            <div className="flex items-center gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleToggleVisibility}
                className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                  publication.est_masquee
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-yellow-600 text-white hover:bg-yellow-700'
                }`}
              >
                {publication.est_masquee ? <Eye size={18} /> : <EyeOff size={18} />}
                {publication.est_masquee ? 'Afficher' : 'Masquer'} la publication
              </button>
              <button
                onClick={handleDeletePublication}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 size={18} />
                Supprimer la publication
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicationDetail;
